import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";
import {
  AnalyticsServiceV2,
  type DailyRequestMetrics,
} from "@/services/features/AnalyticsServiceV2";
import type { Store } from "@/services/features/StoreService";
import {
  type DateRange,
  storePerformanceService,
  type TrendComparisonWindow,
} from "@/services/StorePerformanceService";
import { getBrowserTimezone } from "@/utils/storeTime";

export type ClickAndCollectChartMetric = "requests" | "responseTime" | "missedPercentage";

export interface ClickAndCollectReportDay {
  date: string;
  requests: number;
  missedRequests: number;
  totalTimeTakenSeconds: number;
}

export interface ClickAndCollectReportData {
  current: ClickAndCollectReportDay[];
  comparison: ClickAndCollectReportDay[];
}

const emptyData: ClickAndCollectReportData = { current: [], comparison: [] };

const createDays = (range: DateRange, timezone: string) => {
  const start = moment(range.start).tz(timezone).startOf("day");
  const end = moment(range.end).tz(timezone).startOf("day");
  const days: string[] = [];

  for (const day = start.clone(); day.isSameOrBefore(end, "day"); day.add(1, "day")) {
    days.push(day.format("YYYY-MM-DD"));
  }

  return days;
};

interface UseClickAndCollectReportOptions {
  stores: Store[] | null;
  currentRange: DateRange;
  comparisonWindow: TrendComparisonWindow;
  anchorComparisonToWeekdays?: boolean;
  deviceType: DeviceType;
}

export const useClickAndCollectReport = ({
  stores,
  currentRange,
  comparisonWindow,
  anchorComparisonToWeekdays = true,
  deviceType,
}: UseClickAndCollectReportOptions) => {
  const browserTimezone = getBrowserTimezone();
  const beginBaseRequest = useLatestRequestGuard();
  const storeIdsKey =
    stores
      ?.map((store) => store.id)
      .sort((first, second) => first - second)
      .join(",") ?? "";
  const [data, setData] = useState<ClickAndCollectReportData>(emptyData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const comparisonRange = useMemo(
    () =>
      storePerformanceService.getTrendComparisonRange(
        currentRange,
        comparisonWindow,
        browserTimezone,
        anchorComparisonToWeekdays,
      ),
    [anchorComparisonToWeekdays, browserTimezone, comparisonWindow, currentRange],
  );
  const startDate = storePerformanceService.formatDate(currentRange.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(currentRange.end, browserTimezone);
  const hasRequestInputs = Boolean(storeIdsKey && startDate && endDate);
  const requestKey = hasRequestInputs
    ? `${storeIdsKey}:${startDate}:${endDate}:${comparisonWindow}:${anchorComparisonToWeekdays}:${deviceType}`
    : null;

  useEffect(() => {
    const isCurrent = beginBaseRequest();
    const storeIds = storeIdsKey ? storeIdsKey.split(",").map(Number) : [];

    if (!storeIds.length || !startDate || !endDate) {
      setData(emptyData);
      setLoading(false);
      setError(false);
      setCompletedRequestKey(null);
      return;
    }

    const loadRange = async (range: DateRange): Promise<ClickAndCollectReportDay[]> => {
      const analytics = AnalyticsServiceV2.getInstance();
      const days = createDays(range, browserTimezone);
      const response = await analytics.getDailyRequestAnalytics({
        storeIds,
        startDate: storePerformanceService.formatDate(range.start, browserTimezone),
        endDate: storePerformanceService.formatDate(range.end, browserTimezone),
        requestType: "CLICK_AND_COLLECT",
        ...(deviceType === "all" ? {} : { deviceType }),
      });
      const metricsByDate = new Map(
        (response.data ?? []).map((metric: DailyRequestMetrics) => [metric.date, metric]),
      );

      return days.map((date) => {
        const metric = metricsByDate.get(date);
        return {
          date,
          requests: metric?.totalRequests ?? 0,
          missedRequests: metric?.totalMissedRequests ?? 0,
          totalTimeTakenSeconds: metric?.totalTimeTakenSeconds ?? 0,
        };
      });
    };

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const current = await loadRange(currentRange);
        const comparison = await loadRange(comparisonRange);
        if (isCurrent()) {
          setData({ current, comparison });
          setCompletedRequestKey(requestKey);
        }
      } catch (loadError) {
        console.error("Failed to load click and collect report:", loadError);
        if (isCurrent()) {
          setData(emptyData);
          setError(true);
          setCompletedRequestKey(requestKey);
        }
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    void load();
  }, [
    beginBaseRequest,
    browserTimezone,
    comparisonRange,
    currentRange,
    deviceType,
    endDate,
    requestKey,
    startDate,
    storeIdsKey,
  ]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;

  return {
    comparisonRange,
    data,
    error: error && completedRequestKey === requestKey,
    loading: loading || isRequestPending,
  };
};
